import { useCallback, useEffect, useState, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import Select from "react-select";
import { useSelector, useDispatch } from "react-redux";
import { setSortOrder, setSortBy, setFilterField } from "../../app/itemGridPreferencesSlice";
import APIs from "../../Constants/apis";
import axiosInstance from "../../config/axiosApi/axiosApi";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast from "../../components/CustomComponents/toast/toast";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import moment from "moment";
import Pagination from "../../components/CustomComponents/Pagination/pagination";
import "./../order/ManageOrderFilters.css";

const RiderReport = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;

  const pageKey = "riderReport";
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ value: "20", label: "20" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [locations, setLocations] = useState([]);
  const [riderOptions, setRiderOptions] = useState([{ value: "all", label: "All Riders" }]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingRiders, setLoadingRiders] = useState(false);
  const [loading, setLoading] = useState(false);
  const [riders, setRiders] = useState([]);

  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "RiderName", label: "Rider Name" };
  const parentIdForLocation = isMain && CompID != null ? (typeof CompID === "number" ? CompID : parseInt(CompID, 10)) : null;
  const companyName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";
  const defaultLocation = { value: "all", label: "All Locations" };
  const defaultRider = { value: "all", label: "All Riders" };

  const defaultDateRange = [
    moment().startOf("week").toDate(),
    moment().endOf("week").toDate(),
  ];

  const toValidDate = (v) => {
    if (v == null) return null;
    const d = typeof v === "string" ? new Date(v) : v;
    return d instanceof Date && !isNaN(d.getTime()) ? d : null;
  };

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const location = preferences.location || defaultLocation;
  const rider = preferences.rider || defaultRider;
  const storedDateRange = preferences.dateRange;
  const dateRange = (() => {
    if (!storedDateRange || !Array.isArray(storedDateRange)) return defaultDateRange;
    const start = toValidDate(storedDateRange[0]);
    const end = toValidDate(storedDateRange[1]);
    if (!start && !end) return defaultDateRange;
    return [
      start || moment().startOf("week").toDate(),
      end || moment().endOf("week").toDate(),
    ];
  })();
  const [rawStartDate, rawEndDate] = dateRange;
  const startTime = rawStartDate ? moment(rawStartDate).format("HH:mm") : "00:00";
  const endTime = rawEndDate ? moment(rawEndDate).format("HH:mm") : "23:59";

  const dateRangeKey =
    rawStartDate && rawEndDate
      ? `${moment(rawStartDate).format("YYYY-MM-DD HH:mm")}-${moment(rawEndDate).format("YYYY-MM-DD HH:mm")}`
      : "";

  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const themeColor = useThemeColor();
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();

  const currencySymbols = { GBP: "£", USD: "$", EUR: "€" };
  const getCurrencySymbol = (code) => currencySymbols[code] || code;

  const sortByOptions = [
    { value: "RiderName", label: "Rider Name" },
    { value: "TotalOrdersAssigned", label: "Assigned Orders (Count)" },
    { value: "TotalOrderAmount", label: "Assigned Orders (Amount)" },
    { value: "TotalAssignedOrderAmount", label: "Assigned Orders Total Amount" },
    { value: "CompletedOrders", label: "Completed (Count)" },
    { value: "CompletedOrderAmount", label: "Completed (Amount)" },
    { value: "IncompleteOrderCount", label: "In process (Count)" },
    { value: "IncompleteOrderAmount", label: "In process (Amount)" },
    { value: "TotalCODOrders", label: "COD All (Count)" },
    { value: "TotalCODOrderAmount", label: "COD All (Amount)" },
    { value: "CODIncompleteCount", label: "Cash in Hand (Count)" },
    { value: "CODIncompleteAmount", label: "Cash in Hand (Amount)" },
    { value: "RefundedOrders", label: "Refunded (Count)" },
    { value: "RefundedAmount", label: "Refunded (Amount)" },
    { value: "CancelledOrders", label: "Cancelled (Count)" },
    { value: "CancelledAmount", label: "Cancel (Amount)" },
  ];

  const allLocationsOption = { value: "all", label: "All Locations" };
  const locationOptions =
    locations.length > 0
      ? [allLocationsOption, ...locations]
      : isMain && parentIdForLocation != null && !isNaN(parentIdForLocation)
        ? [allLocationsOption, { value: parentIdForLocation, label: companyName }]
        : [allLocationsOption];

  const handleFilterChange = (filterName, selectedOption) => {
    dispatch(setFilterField({ pageKey, field: filterName, value: selectedOption }));
    setPageNumber(1);
  };

  const resetFilters = () => {
    dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    dispatch(setFilterField({ pageKey, field: "rider", value: defaultRider }));
    dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
  };

  const clearAllFilters = () => {
    resetFilters();
    setPageNumber(1);
  };

  const handleDateRangeChange = (dates) => {
    const [startDate, endDate] = Array.isArray(dates) ? dates : [dates, null];
    let effectiveStartDate = startDate ? (typeof startDate === "string" ? new Date(startDate) : startDate) : null;
    let effectiveEndDate = endDate ? (typeof endDate === "string" ? new Date(endDate) : endDate) : null;
    if (effectiveStartDate && effectiveEndDate && effectiveStartDate > effectiveEndDate) {
      [effectiveStartDate, effectiveEndDate] = [effectiveEndDate, effectiveStartDate];
    } else if (effectiveStartDate && !effectiveEndDate) {
      effectiveEndDate = new Date(effectiveStartDate.getTime());
      effectiveEndDate.setHours(23, 59, 59, 999);
    }
    dispatch(
      setFilterField({
        pageKey,
        field: "dateRange",
        value: [effectiveStartDate, effectiveEndDate],
      })
    );
    setPageNumber(1);
  };

  const fetchRidersReport = useCallback(async () => {
    setLoading(true);
    try {
      const toDate = (v) => {
        if (v == null) return null;
        const d = typeof v === "string" ? new Date(v) : v;
        return d instanceof Date && !isNaN(d.getTime()) ? d : null;
      };
      let startDate = toDate(rawStartDate);
      let endDate = toDate(rawEndDate);
      if (startDate && !endDate) {
        endDate = moment(startDate).endOf("day").toDate();
        startDate = moment(startDate).startOf("day").toDate();
      } else if (endDate && !startDate) {
        startDate = moment(endDate).startOf("day").toDate();
        endDate = moment(endDate).endOf("day").toDate();
      }
      const formattedStartDate = startDate ? moment(startDate).format("YYYY-MM-DD HH:mm:ss") : null;
      const formattedEndDate = endDate ? moment(endDate).format("YYYY-MM-DD HH:mm:ss") : null;

      let locationFilterValue = null;
      if (isMain && location.value !== "all" && location.value != null && location.value !== undefined) {
        const locValue = typeof location.value === "number" ? location.value : parseInt(location.value, 10);
        if (!isNaN(locValue) && locValue > 0) locationFilterValue = locValue;
      }

      const riderFilterValue = rider.value !== "all" ? rider.value : null;

      const params = {
        CompID,
        SortBy: sortBy.value,
        SortOrder: sortOrder.value,
        PageNumber: pageNumber,
        PageSize: pageSize.value,
        StartDate: formattedStartDate,
        EndDate: formattedEndDate,
        LocationFilter: locationFilterValue,
        RiderFilter: riderFilterValue,
      };

      const response = await axiosInstance.get(APIs.GET_RIDER_REPORT, { params });
      let data = response.data?.data ?? response.data ?? [];
      if (!Array.isArray(data)) data = [];
      setRiders(data);
      setTotalRecords(response.data?.totalRecords ?? response.data?.TotalRecords ?? 0);
      setTotalPages(response.data?.totalPages ?? response.data?.TotalPages ?? 0);
    } catch (error) {
      console.error("Error fetching rider report:", error);
      showErrorToast(error.response?.data?.message || error.message || "Failed to fetch rider report.");
      setRiders([]);
      setTotalRecords(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [CompID, isMain, dateRangeKey, location, rider, sortBy, sortOrder, pageNumber, pageSize]);

  const fetchLocations = useCallback(async () => {
    if (!isMain || !CompID) return;
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, { params: { CompID } });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const parentId = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
      const compName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";
      const base = [{ value: parentId, label: compName }];
      const branchOpts = arr
        .filter((loc) => {
          const pid = loc.ParentComID ?? loc.parentComID;
          const id = loc.CompID ?? loc.compID;
          const pidNum = pid != null ? (typeof pid === "number" ? pid : parseInt(pid, 10)) : null;
          const idNum = id != null ? (typeof id === "number" ? id : parseInt(id, 10)) : null;
          if (idNum == null || isNaN(idNum)) return false;
          return pidNum === parentId && idNum !== parentId;
        })
        .map((loc) => {
          const id = loc.CompID ?? loc.compID;
          const name = String(loc.CompName ?? loc.compName ?? "").trim() || `Branch ${id ?? ""}`;
          const val = typeof id === "number" ? id : parseInt(id, 10);
          return { value: isNaN(val) ? null : val, label: name };
        })
        .filter((o) => o.value != null);
      setLocations([...base, ...branchOpts]);
    } catch (error) {
      console.error("Error fetching locations:", error);
      showErrorToast(error.response?.data?.message || "Error loading locations");
    } finally {
      setLoadingLocations(false);
    }
  }, [CompID, isMain, profileData?.compName, profileData?.CompName]);

  const fetchRiders = useCallback(async () => {
    try {
      setLoadingRiders(true);
      const staffResponse = await axiosInstance.get(APIs.GET_ALL_STAFF, { params: { CompID } });
      const staffData = staffResponse.data?.data ?? staffResponse.data ?? [];
      let riderRoleIds = new Set();
      try {
        const rolesResponse = await axiosInstance.get(APIs.GET_ROLES, { params: { CompID } });
        const rolesData = rolesResponse.data?.data ?? rolesResponse.data ?? [];
        rolesData.forEach((role) => {
          if (role.r_Title && role.r_Title.toLowerCase() === "rider") {
            riderRoleIds.add(role.comRolePermission_ID ?? role.ComRolePermission_ID);
          }
        });
      } catch (roleError) {
        console.error("Error fetching roles:", roleError);
      }
      const ridersOnly = staffData.filter((member) => {
        const roleId = member.comRolePermission_ID ?? member.ComRolePermission_ID ?? member.userRoles?.comRolePermission_ID;
        return riderRoleIds.has(roleId);
      });
      const fullName = (m) =>
        (`${m.firstName ?? m.FirstName ?? ""} ${m.lastName ?? m.LastName ?? ""}`.trim()) || (m.userName ?? m.UserName ?? "");
      const userName = (m) => m.userName ?? m.UserName ?? "";
      const options = [
        { value: "all", label: "All Riders" },
        ...ridersOnly.map((m) => ({
          value: userName(m),
          label: fullName(m) || userName(m),
        })),
      ];
      setRiderOptions(options);
    } catch (error) {
      console.error("Error fetching riders:", error);
      showErrorToast(error.response?.data?.message || "Error loading riders");
      setRiderOptions([{ value: "all", label: "All Riders" }]);
    } finally {
      setLoadingRiders(false);
    }
  }, [CompID]);

  const initRanRef = useRef(false);
  useEffect(() => {
    if (initRanRef.current) return;
    initRanRef.current = true;
    if (!preferences.sortOrder) dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    if (!preferences.sortBy) dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    if (!preferences.location) dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    if (!preferences.rider) dispatch(setFilterField({ pageKey, field: "rider", value: defaultRider }));
    const needsDefaultDate =
      !storedDateRange ||
      !Array.isArray(storedDateRange) ||
      !toValidDate(storedDateRange[0]) ||
      !toValidDate(storedDateRange[1]);
    if (needsDefaultDate) dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
  }, [dispatch, pageKey]);

  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    fetchRidersReport();
    setHasMounted(true);
  }, []);
  useEffect(() => {
    if (hasMounted) fetchRidersReport();
  }, [location, rider, dateRangeKey, pageNumber, pageSize.value, sortBy, sortOrder, fetchRidersReport]);

  useEffect(() => {
    if (isMain) fetchLocations();
    fetchRiders();
  }, [isMain, fetchLocations, fetchRiders]);

  const formatCurrency = (amount) => {
    const sym = getCurrencySymbol(compCurrency);
    const num = parseFloat(amount);
    if (isNaN(num)) return sym + "0.00";
    return sym + num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const getRiderDisplayName = (riderNameOrUserName) => {
    if (!riderNameOrUserName) return "—";
    const option = riderOptions.find((o) => o.value === riderNameOrUserName);
    return option ? option.label : riderNameOrUserName;
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <a href="#/report">Report</a>
            </li>
            <li className="breadcrumb-item active">Rider Report</li>
          </ol>
          <h1 className="page-header mb-0">Rider Report</h1>
        </div>
      </div>

      <div
        className="card mb-4"
        style={{
          position: "relative",
          zIndex: 10,
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
          borderRadius: "12px",
          border: "none",
          boxShadow: `0 4px 20px ${themeColor}40`,
        }}
      >
        <div className="card-body p-3">
          <div className="row g-3 align-items-center mb-3">
            <div className="col-xl-3 col-lg-4 col-md-6">
              <div className="filter-control" style={{ position: "relative", zIndex: 9999, width: "100%" }}>
                <DateRangePicker
                  startDate={toValidDate(rawStartDate)}
                  endDate={toValidDate(rawEndDate)}
                  onChange={handleDateRangeChange}
                  placeholder="Select Date Range"
                  className="filter-dropdown"
                  allowSingleDate={true}
                  allowDateRange={true}
                  showTimeSelection={true}
                />
              </div>
            </div>
            <div className="col-xl-2 col-lg-4 col-md-6">
              <div className="d-flex gap-2 justify-content-end">
                <button className="clear-btn" onClick={clearAllFilters} title="Clear all filters">
                  <i className="fa fa-times me-1"></i>
                  Filter
                </button>
              </div>
            </div>
          </div>
          <div className="row g-3 align-items-center">
            {isMain && locationOptions.length > 1 && (
              <div className="col-xl-2 col-lg-4 col-md-6">
                <div className="filter-control">
                  <Select
                    value={location}
                    onChange={(option) => handleFilterChange("location", option)}
                    options={locationOptions}
                    styles={dropdownStyles}
                    isSearchable={false}
                    isLoading={loadingLocations}
                    placeholder="Location"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuShouldBlockScroll={false}
                    closeMenuOnScroll={false}
                  />
                </div>
              </div>
            )}
            <div className="col-xl-3 col-lg-4 col-md-6">
              <div className="filter-control">
                <Select
                  value={rider}
                  onChange={(option) => handleFilterChange("rider", option)}
                  options={riderOptions}
                  styles={dropdownStyles}
                  isSearchable={true}
                  isLoading={loadingRiders}
                  placeholder="Rider"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>
            <div className="col-xl-2 col-lg-4 col-md-6">
              <div className="filter-control">
                <Select
                  value={sortBy}
                  onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                  options={sortByOptions}
                  styles={dropdownStyles}
                  placeholder="Sort By"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>
            <div className="col-xl-2 col-lg-4 col-md-6">
              <div className="filter-control">
                <SortOrderDropDownSelection
                  defaultValue={sortOrder}
                  onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3 mt-2">
        <div className="card border-0">
          <div className="tab-content p-3">
            <div
              className="table-responsive mt-3"
              style={{ minHeight: "200px", maxHeight: "450px", overflowY: "auto", overflowX: "auto" }}
            >
              <table className="table table-hover">
                <thead
                  className="bg-light"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <tr>
                    <th className="pt-2 pb-2">Rider Name</th>
                    <th className="pt-2 pb-2 text-end">Assigned Orders</th>
                    <th className="pt-2 pb-2 text-end">Completed</th>
                    <th className="pt-2 pb-2 text-end">Refunded</th>
                    <th className="pt-2 pb-2 text-end">Cancelled</th>
                    <th className="pt-2 pb-2 text-end">In process</th>
                    <th className="pt-2 pb-2 text-end">COD (All)</th>
                    <th className="pt-2 pb-2 text-end">Cash in Hand</th>
                  </tr>
                </thead>
                <tbody>
                  {riders.length === 0 && !loading && (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        No rider data found for the selected filters.
                      </td>
                    </tr>
                  )}
                  {riders.map((row, index) => {
                    const allCount = row.totalOrdersAssigned ?? row.TotalOrdersAssigned ?? 0;
                    const allAmount = row.totalAssignedOrderAmount ?? row.TotalAssignedOrderAmount ?? row.totalOrderAmount ?? row.TotalOrderAmount ?? 0;
                    const incCount = row.incompleteOrderCount ?? row.IncompleteOrderCount ?? 0;
                    const incAmount = row.incompleteOrderAmount ?? row.IncompleteOrderAmount ?? 0;
                    const codAllCount = row.totalCODOrders ?? row.TotalCODOrders ?? 0;
                    const codAllAmount = row.totalCODOrderAmount ?? row.TotalCODOrderAmount ?? 0;
                    const codIncCount = row.codIncompleteCount ?? row.CODIncompleteCount ?? 0;
                    const codIncAmount = row.codIncompleteAmount ?? row.CODIncompleteAmount ?? 0;
                    const completedCount = row.completedOrders ?? row.CompletedOrders ?? 0;
                    const completedAmount =
                      row.completedOrderAmount ?? row.CompletedOrderAmount ?? 0;
                    const refundCount = row.refundedOrders ?? row.RefundedOrders ?? 0;
                    const refundAmount = row.refundedAmount ?? row.RefundedAmount ?? 0;
                    const cancelCount = row.cancelledOrders ?? row.CancelledOrders ?? 0;
                    const cancelAmount = row.cancelledAmount ?? row.CancelledAmount ?? 0;
                    return (
                      <tr key={(row.riderName ?? row.RiderName ?? "") + index}>
                        <td className="pt-2 pb-2">{getRiderDisplayName(row.riderName ?? row.RiderName)}</td>
                        <td className="pt-2 pb-2 text-end">{formatCurrency(allAmount)} ({allCount})</td>
                        <td className="pt-2 pb-2 text-end">
                          {formatCurrency(completedAmount)} ({completedCount})
                        </td>
                        <td className="pt-2 pb-2 text-end">
                          {formatCurrency(refundAmount)} ({refundCount})
                        </td>
                        <td className="pt-2 pb-2 text-end">
                          {formatCurrency(cancelAmount)} ({cancelCount})
                        </td>
                        <td className="pt-2 pb-2 text-end">{formatCurrency(incAmount)} ({incCount})</td>
                        <td className="pt-2 pb-2 text-end">{formatCurrency(codAllAmount)} ({codAllCount})</td>
                        <td className="pt-2 pb-2 text-end">{formatCurrency(codIncAmount)} ({codIncCount})</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 0 && (
              <Pagination
                totalRecords={totalRecords}
                pageSize={pageSize}
                pageNumber={pageNumber}
                totalPages={totalPages}
                setPageNumber={setPageNumber}
                setPageSize={setPageSize}
                themeColor={themeColor}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiderReport;
